/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SplitTable,
  DropdownButton,
  InvoiceDetails,
  SplitTableRef,
  SendInvoice,
  EditInvoice,
  Button,
  StyledTextInput,
  DialogNotify,
  Dialog,
} from "@components";
import { Customer, Invoice } from "../../models";
import { ITableRow, WorkspaceRole } from "../../types";
import { IInvoiceItem } from "../../types/Invoice";
import {
  HiOutlineChevronDown,
  HiCheck,
  HiOutlineEye,
  HiOutlineDownload,
  HiOutlinePrinter,
  HiOutlinePaperAirplane,
  HiOutlinePencil,
  HiOutlineExclamationCircle,
  HiOutlineCheckCircle,
  HiOutlineRefresh,
  HiOutlineCog,
  HiOutlineTrash,
} from "react-icons/hi";
import {
  discardInvoice,
  sendInvoice,
  resendInvoice,
  previewInvoice,
  downloadInvoice,
  downloadInvoiceCsv,
  updateInvoice,
  type InvoiceDeliveryOptions,
} from "../../api/client";
import { setManualInvoiceAccountingRegistration } from "../../api/invoiceAccounting";
import {
  downloadAccountingInvoiceDocument,
  getAccountingIntegrationCatalog,
  getInvoiceAccountingDetails,
  previewAccountingInvoice,
  reconcileAccountingInvoice,
  submitAccountingInvoice,
  type AccountingConnection,
  type AccountingInvoicePreview,
  type AccountingInvoiceStatus,
} from "../../api";
import { useDataCache } from "../../data/useDataCache";
import { useUserSession } from "../../context/UserContext";
import { Toast } from "flowbite-react";
import { Modal } from "flowbite-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useWorkspace } from "../../context/WorkspaceContext";
import useCompanies from "../../hooks/useCompanies";
import {
  canDiscardInvoice,
  isInvoiceDraft,
  isInvoiceFinalized,
} from "../../helpers/invoiceFilters";
import { canOfferInvoiceDeliveryActions } from "../../helpers/invoiceDelivery";
import { modalPortalRoot } from "../../helpers/modalRoot";
import { InvoiceAccountingStatusBadge } from "../Badges/InvoiceAccountingStatusBadge";
import { InvoiceStatusBadge } from "../Badges/InvoiceStatusBadge";
import { TripletexInvoiceDialog } from "./TripletexInvoiceDialog";

interface InvoiceListProp {
  invoices: Invoice[];
  isLoading: boolean;
  isOpen: (val: boolean) => void;
  onRefresh: () => void;
  tableFooter?: React.ReactNode;
  onInvoiceSelect?: (invoice: Invoice | undefined) => void; // New callback
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

const csvFilename = (invoice: Invoice): string => {
  const customer = invoice.invoicee;
  const customerName = customer?.isCompany
    ? customer.name
    : [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") ||
      customer?.name;
  const slug =
    (customerName || "kunde")
      .toLocaleLowerCase("nb-NO")
      .replace(/æ/g, "ae")
      .replace(/ø/g, "o")
      .replace(/å/g, "a")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "kunde";
  const invoiceNumber =
    String(invoice.invoiceNumber || "ukjent")
      .replace(/[^A-Za-z0-9_-]+/g, "-")
      .replace(/^-|-$/g, "") || "ukjent";
  return `${slug}-faktura-${invoiceNumber}.csv`;
};

const invoiceApiError = (error: unknown, fallback: string): string => {
  const responseError = (error as { response?: { data?: { error?: unknown } } })
    ?.response?.data?.error;
  return typeof responseError === "string" && responseError.trim()
    ? responseError
    : fallback;
};

const INVALID_CUSTOMER_ORGANIZATION_NUMBER =
  "A valid nine-digit customer organization number is required";
const DELETED_ISSUING_COMPANY =
  "The issuing company was deleted. Edit the invoice and select an existing company before sending.";

export const InvoiceList: FC<InvoiceListProp> = function (
  props: InvoiceListProp,
) {
  const { t } = useTranslation(["common", "orders", "validation", "invoice"]);
  const localizedInvoiceApiError = (
    error: unknown,
    fallback: string,
  ): string => {
    const message = invoiceApiError(error, fallback);
    if (message === INVALID_CUSTOMER_ORGANIZATION_NUMBER) {
      return t(
        "invoice:accounting_integration.invalid_customer_organization_number",
      );
    }
    if (message === DELETED_ISSUING_COMPANY) {
      return t("invoice:actions.deleted_issuing_company");
    }
    return message;
  };
  const { isOpen: setIsOpen, onInvoiceSelect } = props;
  const { userSession } = useUserSession();
  const [openedInvoice, setOpenedInvoice] = useState<Invoice | undefined>(
    undefined,
  );
  const [tableRows, setTableRows] = useState([] as ITableRow[]);
  const [hideColumns, setHideColumns] = useState(false);
  const [showSendInvoiceDialog, setShowSendInvoiceDialog] = useState(false);
  const [isRecoveringAccountingDelivery, setIsRecoveringAccountingDelivery] =
    useState(false);
  const [isSending, setIsSending] = useState(false);
  const [successToastText, setSuccessToastText] = useState("");
  const [errorToastText, setErrorToastText] = useState("");
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [showAccountingNumberDialog, setShowAccountingNumberDialog] =
    useState(false);
  const [accountingCustomerNumber, setAccountingCustomerNumber] = useState("");
  const [isSavingAccountingNumber, setIsSavingAccountingNumber] =
    useState(false);
  const splitTableRef = useRef<SplitTableRef>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { invalidateInvoices, invalidateOrders } = useDataCache();
  const [discardTarget, setDiscardTarget] = useState<Invoice>();
  const [isDiscarding, setIsDiscarding] = useState(false);
  const discardRunning = useRef(false);
  const [discardError, setDiscardError] = useState("");
  const [showEditInvoiceDialog, setShowEditInvoiceDialog] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [accountingDetails, setAccountingDetails] =
    useState<AccountingInvoiceStatus | null>(null);
  const [accountingConnection, setAccountingConnection] =
    useState<AccountingConnection | null>(null);
  const [accountingIntegrationAvailable, setAccountingIntegrationAvailable] =
    useState(false);
  const [isLoadingAccountingDetails, setIsLoadingAccountingDetails] =
    useState(false);
  const [isRefreshingAccountingDetails, setIsRefreshingAccountingDetails] =
    useState(false);
  const [tripletexPreview, setTripletexPreview] =
    useState<AccountingInvoicePreview | null>(null);
  const [showTripletexDialog, setShowTripletexDialog] = useState(false);
  const [allowTripletexCustomerCreate, setAllowTripletexCustomerCreate] =
    useState(false);
  const [isPreparingTripletex, setIsPreparingTripletex] = useState(false);
  const [isSubmittingTripletex, setIsSubmittingTripletex] = useState(false);
  const [tripletexFinalizeDraft, setTripletexFinalizeDraft] = useState(false);
  const [tripletexActionError, setTripletexActionError] = useState<
    string | null
  >(null);
  const [
    showAccountingRegistrationDialog,
    setShowAccountingRegistrationDialog,
  ] = useState(false);
  const [
    isUpdatingAccountingRegistration,
    setIsUpdatingAccountingRegistration,
  ] = useState(false);
  const { activeWorkspace } = useWorkspace();
  const { data: companies = [] } = useCompanies(activeWorkspace?.workspaceId);
  const canManageInvoices =
    activeWorkspace?.role === WorkspaceRole.Owner ||
    activeWorkspace?.role === WorkspaceRole.Administrator;
  const canManageAccountingRegistration = canManageInvoices;
  const inferredIssuanceMode:
    | InvoiceDeliveryOptions["issuanceMode"]
    | undefined =
    openedInvoice?.issuanceMode ??
    (openedInvoice?.customerSentAt || openedInvoice?.isSent
      ? "logibud"
      : openedInvoice?.accountingSentAt
        ? "accounting_provider"
        : undefined);
  const hasInvoiceDelivery = Boolean(
    openedInvoice?.customerSentAt ||
    openedInvoice?.accountingSentAt ||
    openedInvoice?.isSent,
  );
  const workspaceId =
    activeWorkspace?.workspaceId || openedInvoice?.workspaceId || "";
  const accessToken = userSession?.token.accessToken;
  const openedInvoiceId = openedInvoice?.id || "";
  const openedInvoiceCompanyId =
    openedInvoice?.companyId || openedInvoice?.invoicer?.id || "";
  const accountingState =
    accountingDetails?.state ??
    openedInvoice?.accountingState ??
    (openedInvoice && isInvoiceFinalized(openedInvoice)
      ? "not_registered"
      : "not_applicable");
  const externalAccountingInvoiceNumber =
    accountingDetails?.externalDocument?.invoiceNumber ??
    openedInvoice?.accountingExternalInvoiceNumber;
  const canCreateInTripletex = Boolean(
    canManageInvoices &&
    accountingIntegrationAvailable &&
    inferredIssuanceMode === "accounting_provider" &&
    isInvoiceFinalized(openedInvoice) &&
    accountingConnection?.providerKey === "tripletex" &&
    accountingConnection.status === "active" &&
    accountingState === "not_registered" &&
    !externalAccountingInvoiceNumber,
  );
  const canRecoverTripletex = Boolean(
    canManageInvoices &&
    accountingIntegrationAvailable &&
    inferredIssuanceMode === "accounting_provider" &&
    isInvoiceFinalized(openedInvoice) &&
    accountingConnection?.providerKey === "tripletex" &&
    accountingConnection.status === "active" &&
    accountingDetails?.recovery?.canRetry &&
    !externalAccountingInvoiceNumber,
  );
  const hasIntegrationAccountingRecord = Boolean(
    accountingDetails?.mode === "integration" ||
    accountingDetails?.providerKey ||
    openedInvoice?.accountingMode === "integration",
  );
  const canOfferDeliveryActions = Boolean(
    openedInvoice &&
    canOfferInvoiceDeliveryActions(
      openedInvoice,
      hasIntegrationAccountingRecord,
    ),
  );
  const canIssueDraftThroughTripletex = Boolean(
    canManageInvoices &&
    accountingIntegrationAvailable &&
    openedInvoice &&
    isInvoiceDraft(openedInvoice) &&
    !hasInvoiceDelivery &&
    accountingConnection?.providerKey === "tripletex" &&
    accountingConnection.status === "active",
  );

  useEffect(() => {
    let cancelled = false;
    if (!openedInvoiceId || !accessToken || !workspaceId) {
      setAccountingDetails(null);
      setAccountingConnection(null);
      setAccountingIntegrationAvailable(false);
      setIsLoadingAccountingDetails(false);
      return;
    }

    setIsLoadingAccountingDetails(true);
    setAccountingDetails(null);
    setAccountingConnection(null);
    setAccountingIntegrationAvailable(false);

    void Promise.allSettled([
      getInvoiceAccountingDetails(accessToken, workspaceId, openedInvoiceId),
      openedInvoiceCompanyId && canManageInvoices
        ? getAccountingIntegrationCatalog(
            accessToken,
            workspaceId,
            openedInvoiceCompanyId,
          )
        : Promise.resolve([]),
    ]).then(([statusResult, catalogResult]) => {
      if (cancelled) return;
      if (statusResult.status === "fulfilled") {
        setAccountingDetails(statusResult.value);
      }
      if (catalogResult.status === "fulfilled") {
        const integration = catalogResult.value.find(
          (item) => item.providerKey === "tripletex",
        );
        setAccountingIntegrationAvailable(
          Boolean(integration && integration.availability !== "coming_soon"),
        );
        setAccountingConnection(integration?.connection || null);
      }
      setIsLoadingAccountingDetails(false);
    });

    return () => {
      cancelled = true;
    };
  }, [
    accessToken,
    canManageInvoices,
    openedInvoiceCompanyId,
    openedInvoiceId,
    workspaceId,
  ]);

  const openSendInvoiceDialog = () => {
    setIsRecoveringAccountingDelivery(false);
    setShowSendInvoiceDialog(true);
  };

  const closeSendInvoiceDialog = () => {
    setIsRecoveringAccountingDelivery(false);
    setShowSendInvoiceDialog(false);
  };

  const toggleAccountingRegistration = async () => {
    if (!openedInvoice || !userSession) return;
    const registered = openedInvoice.accountingState !== "confirmed";
    setIsUpdatingAccountingRegistration(true);
    try {
      const updated = await setManualInvoiceAccountingRegistration(
        userSession.token.accessToken,
        openedInvoice.id,
        registered,
        openedInvoice.workspaceId,
      );
      setOpenedInvoice(new Invoice(updated));
      setSuccessToastText(
        t(
          registered
            ? "invoice:actions.accounting_registration_success"
            : "invoice:actions.accounting_registration_reverted",
        ),
      );
      setShowAccountingRegistrationDialog(false);
      props.onRefresh();
    } catch (error) {
      console.error("Error updating invoice accounting registration:", error);
      setErrorToastText(t("invoice:actions.accounting_registration_error"));
    } finally {
      setIsUpdatingAccountingRegistration(false);
    }
  };

  const refreshAccountingDetails = async () => {
    if (!openedInvoice || !accessToken || !workspaceId) return;
    setIsRefreshingAccountingDetails(true);
    try {
      if (hasIntegrationAccountingRecord) {
        await reconcileAccountingInvoice(
          accessToken,
          workspaceId,
          openedInvoice.id,
        );
      }
      const latest = await getInvoiceAccountingDetails(
        accessToken,
        workspaceId,
        openedInvoice.id,
      );
      setAccountingDetails(latest);
      setSuccessToastText(t("invoice:accounting_integration.refresh_success"));
      props.onRefresh();
    } catch (error) {
      const message = localizedInvoiceApiError(
        error,
        t("invoice:accounting_integration.refresh_error"),
      );
      setErrorToastText(message);
    } finally {
      setIsRefreshingAccountingDetails(false);
    }
  };

  const prepareTripletexInvoice = async (
    finalizeDraft = false,
  ): Promise<boolean> => {
    if (!openedInvoice || !accessToken || !workspaceId) return false;
    setIsPreparingTripletex(true);
    setTripletexActionError(null);
    try {
      const preview = await previewAccountingInvoice(
        accessToken,
        workspaceId,
        openedInvoice.id,
        false,
        finalizeDraft,
      );
      setTripletexPreview(preview);
      setTripletexFinalizeDraft(finalizeDraft);
      setAllowTripletexCustomerCreate(false);
      setShowTripletexDialog(true);
      return true;
    } catch (error) {
      setErrorToastText(
        localizedInvoiceApiError(
          error,
          t("invoice:accounting_integration.preview_error"),
        ),
      );
      return false;
    } finally {
      setIsPreparingTripletex(false);
    }
  };

  const createTripletexInvoice = async () => {
    if (!openedInvoice || !accessToken || !workspaceId || !tripletexPreview) {
      return;
    }
    if (
      tripletexPreview.customer.resolution === "missing" &&
      !allowTripletexCustomerCreate
    ) {
      return;
    }

    setIsSubmittingTripletex(true);
    setTripletexActionError(null);
    try {
      await submitAccountingInvoice(
        accessToken,
        workspaceId,
        openedInvoice.id,
        allowTripletexCustomerCreate,
        tripletexFinalizeDraft,
      );
      const latest = await getInvoiceAccountingDetails(
        accessToken,
        workspaceId,
        openedInvoice.id,
      );
      setAccountingDetails(latest);
      setShowTripletexDialog(false);
      setTripletexPreview(null);
      setTripletexFinalizeDraft(false);
      setSuccessToastText(
        t(
          tripletexPreview.recovery?.stage
            ? "invoice:accounting_integration.recovery_success"
            : "invoice:accounting_integration.create_success",
        ),
      );
      props.onRefresh();
    } catch (error) {
      setTripletexActionError(
        localizedInvoiceApiError(
          error,
          t("invoice:accounting_integration.create_error"),
        ),
      );
    } finally {
      setIsSubmittingTripletex(false);
    }
  };

  const downloadCsv = async (invoice: Invoice) => {
    if (!userSession) return;
    setIsDownloadingCsv(true);
    try {
      const csv = await downloadInvoiceCsv(
        userSession.token.accessToken,
        invoice.id,
      );
      if (csv instanceof Blob) {
        const url = window.URL.createObjectURL(csv);
        const link = document.createElement("a");
        link.href = url;
        link.download = csvFilename(invoice);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading invoice CSV:", error);
      setErrorToastText(t("invoice:actions.download_csv_error"));
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (!openedInvoice || !userSession) return;
    const customerId = openedInvoice.customerId || openedInvoice.invoicee?.id;
    let currentCustomer = openedInvoice.invoicee
      ? new Customer(openedInvoice.invoicee)
      : undefined;
    if (customerId) {
      try {
        currentCustomer = await Customer.getById(
          userSession.token.accessToken,
          openedInvoice.workspaceId,
          customerId,
        );
      } catch (error) {
        console.error("Error loading invoice customer:", error);
        setErrorToastText(t("invoice:actions.customer_load_error"));
        return;
      }
    }
    if (!currentCustomer?.accountingCustomerNumber) {
      setAccountingCustomerNumber("");
      setShowAccountingNumberDialog(true);
      return;
    }
    const currentInvoice = new Invoice({
      ...openedInvoice,
      invoicee: currentCustomer,
    });
    setOpenedInvoice(currentInvoice);
    await downloadCsv(currentInvoice);
  };

  const saveAccountingNumberAndDownload = async () => {
    if (!openedInvoice?.invoicee) return;
    const normalized = accountingCustomerNumber.trim();
    if (!/^[A-Za-z0-9._-]{1,50}$/.test(normalized)) {
      setErrorToastText(t("invoice:actions.accounting_customer_number_error"));
      return;
    }
    const customerId = openedInvoice.customerId || openedInvoice.invoicee.id;
    if (!customerId) {
      setErrorToastText(t("invoice:actions.customer_update_error"));
      return;
    }

    setIsSavingAccountingNumber(true);
    try {
      const updatedCustomer = await Customer.update(
        new Customer({
          ...openedInvoice.invoicee,
          id: customerId,
          workspaceId:
            openedInvoice.invoicee.workspaceId || openedInvoice.workspaceId,
        }),
        { accountingCustomerNumber: normalized },
      );
      const updatedInvoice = new Invoice({
        ...openedInvoice,
        invoicee: updatedCustomer,
      });
      setOpenedInvoice(updatedInvoice);
      setShowAccountingNumberDialog(false);
      await downloadCsv(updatedInvoice);
    } catch (error) {
      console.error("Error saving accounting customer number:", error);
      setErrorToastText(t("invoice:actions.customer_update_error"));
    } finally {
      setIsSavingAccountingNumber(false);
    }
  };

  const updateOpenedInvoice = useCallback(
    (invoice: Invoice | undefined) => {
      setOpenedInvoice(invoice);
      if (invoice !== undefined) {
        setHideColumns(true);
        setIsOpen(true);
        const currentParams = new URLSearchParams(searchParams);
        currentParams.set("id", invoice.id);
        navigate(
          { pathname: "/invoices", search: currentParams.toString() },
          { replace: true },
        );
      } else {
        setHideColumns(false);
        setIsOpen(false);
        const currentParams = new URLSearchParams(searchParams);
        currentParams.delete("id");
        navigate(
          { pathname: "/invoices", search: currentParams.toString() },
          { replace: true },
        );
      }

      onInvoiceSelect?.(invoice);
    },
    [navigate, onInvoiceSelect, searchParams, setIsOpen],
  );

  const getTotalPrice = (invoice: Invoice) => {
    // Prefer the authoritative amount used by the detail view and export so the
    // list total matches everywhere. Fall back to recomputing from the lines,
    // using discountInPercent (with legacy discount as a fallback).
    if (invoice.amounts?.totalAmount != null) {
      return invoice.amounts.totalAmount;
    }

    return invoice.items.reduce((accumulator, item) => {
      let itemTotal = item.quantity * item.price;

      const discountPercent = item.discountInPercent ?? item.discount ?? 0;
      if (discountPercent) {
        itemTotal -= (itemTotal * discountPercent) / 100;
      }

      // Apply VAT if any
      if (item.vat) {
        itemTotal += (itemTotal * item.vat) / 100;
      }

      return accumulator + itemTotal;
    }, 0);
  };

  useEffect(() => {
    if (!props.isLoading && props.invoices) {
      // Sort invoices by invoiceDate (newest first)
      const sortedInvoices = [...props.invoices].sort((a, b) => {
        // Compare dates directly, with fallback to creation date
        const dateA = new Date(a.invoiceDate || a.createdAt);
        const dateB = new Date(b.invoiceDate || b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
      setTableRows(
        sortedInvoices.map((invoice) => {
          return {
            id: invoice.id,
            isActive: invoice.id === openedInvoice?.id,
            onRowClickData: invoice,
            cells: [
              {
                id: "invoice_number",
                children: invoice.invoiceNumber ?? "-",
                showOnSmallScreen: true,
              },
              {
                id: "customer",
                children: invoice.invoicee?.isCompany
                  ? invoice.invoicee?.name
                  : invoice.invoicee?.firstName +
                    " " +
                    invoice.invoicee?.lastName,
                showOnSmallScreen: true,
              },
              {
                id: "status",
                children: (
                  <InvoiceStatusBadge
                    invoice={invoice}
                    accountingSettlementStatus={
                      invoice.id === openedInvoice?.id
                        ? accountingDetails?.settlement?.status
                        : undefined
                    }
                  />
                ),
                showOnSmallScreen: true,
              },
              {
                id: "accounting_status",
                children: <InvoiceAccountingStatusBadge invoice={invoice} />,
                showOnSmallScreen: false,
                hidden: openedInvoice !== undefined,
              },
              {
                id: "due_date",
                children:
                  invoice.issuanceMode === "accounting_provider"
                    ? "—"
                    : formatDate(invoice.dueDate),
                showOnSmallScreen: true,
                hidden: hideColumns,
              },
              {
                id: "sum_total",
                children:
                  "NOK " + getTotalPrice(invoice)?.toFixed(2).replace(".", ","),
                showOnSmallScreen: false,
                hidden: openedInvoice !== undefined,
              },
            ],
          };
        }),
      );
    }
  }, [
    props.isLoading,
    props.invoices,
    t,
    openedInvoice,
    hideColumns,
    accountingDetails?.settlement?.status,
  ]);

  useEffect(() => {
    const invoiceIdFromUrl = searchParams.get("id");

    if (invoiceIdFromUrl && props.invoices.length > 0) {
      const freshInvoiceInstance = props.invoices.find(
        (inv) => inv.id === invoiceIdFromUrl,
      );

      if (freshInvoiceInstance) {
        if (
          !openedInvoice ||
          openedInvoice.id !== freshInvoiceInstance.id ||
          JSON.stringify(openedInvoice.items) !==
            JSON.stringify(freshInvoiceInstance.items) ||
          openedInvoice.lifecycleStatus !==
            freshInvoiceInstance.lifecycleStatus ||
          openedInvoice.issuanceMode !== freshInvoiceInstance.issuanceMode ||
          openedInvoice.isSent !== freshInvoiceInstance.isSent ||
          openedInvoice.customerSentAt !==
            freshInvoiceInstance.customerSentAt ||
          openedInvoice.accountingSentAt !==
            freshInvoiceInstance.accountingSentAt ||
          openedInvoice.accountingState !==
            freshInvoiceInstance.accountingState ||
          openedInvoice.accountingMode !==
            freshInvoiceInstance.accountingMode ||
          openedInvoice.accountingExternalInvoiceNumber !==
            freshInvoiceInstance.accountingExternalInvoiceNumber ||
          openedInvoice.accountingSettlementStatus !==
            freshInvoiceInstance.accountingSettlementStatus ||
          openedInvoice.accountingAmountOutstanding !==
            freshInvoiceInstance.accountingAmountOutstanding ||
          openedInvoice.accountingLastSyncedAt !==
            freshInvoiceInstance.accountingLastSyncedAt ||
          openedInvoice.accountingLastErrorCode !==
            freshInvoiceInstance.accountingLastErrorCode
        ) {
          setOpenedInvoice(freshInvoiceInstance);
        }
        // If an invoice is supposed to be open (based on URL and found instance), ensure UI reflects this.
        setHideColumns(true);
        setIsOpen(true);
        splitTableRef.current?.setSplit(true);
      } else {
        if (openedInvoice && openedInvoice.id === invoiceIdFromUrl) {
          updateOpenedInvoice(undefined);
        }
      }
    } else if (!invoiceIdFromUrl && openedInvoice) {
      updateOpenedInvoice(undefined);
    }
  }, [
    props.invoices,
    searchParams,
    openedInvoice,
    setIsOpen,
    updateOpenedInvoice,
    setHideColumns,
  ]);

  const handleSendInvoice = async (delivery: InvoiceDeliveryOptions) => {
    if (!openedInvoice || !userSession) return;

    if (
      delivery.issuanceMode === "accounting_provider" &&
      canIssueDraftThroughTripletex
    ) {
      const prepared = await prepareTripletexInvoice(true);
      if (prepared) {
        closeSendInvoiceDialog();
      }
      return;
    }

    setIsSending(true);
    try {
      if (hasInvoiceDelivery || isRecoveringAccountingDelivery) {
        await resendInvoice(
          userSession.token.accessToken,
          openedInvoice.id,
          delivery,
          activeWorkspace?.workspaceId,
        );
      } else {
        await sendInvoice(
          userSession.token.accessToken,
          openedInvoice.id,
          delivery,
          activeWorkspace?.workspaceId,
        );
      }
      closeSendInvoiceDialog();
      setSuccessToastText(
        t(
          delivery.issuanceMode === "accounting_provider"
            ? "invoice:actions.accounting_basis_send_success"
            : "invoice:actions.send_success",
        ),
      );
      props.onRefresh();
    } catch (error) {
      const apiError = error as {
        response?: { data?: { error?: string; errorCode?: string } };
      };
      if (
        apiError.response?.data?.errorCode ===
        "INVOICE_CUSTOMER_SENT_ACCOUNTING_FAILED"
      ) {
        setIsRecoveringAccountingDelivery(true);
        setErrorToastText(t("invoice:actions.accounting_copy_delivery_failed"));
        props.onRefresh();
        return;
      }
      setErrorToastText(
        localizedInvoiceApiError(
          error,
          t("invoice:actions.send_error", "Could not send the invoice"),
        ),
      );
      console.error("Error sending invoice:", error);
      props.onRefresh();
    } finally {
      setIsSending(false);
    }
  };

  const loadInvoicePreview = async (): Promise<string> => {
    if (!openedInvoice || !userSession) {
      throw new Error("Invoice preview requires an active invoice and session");
    }
    const html = await previewInvoice(
      userSession.token.accessToken,
      openedInvoice.id,
    );
    if (typeof html !== "string" || !html.trim()) {
      throw new Error("Invalid HTML content received");
    }
    return html;
  };

  const loadProviderDocument = async (): Promise<Blob> => {
    if (!openedInvoice || !userSession || !activeWorkspace?.workspaceId) {
      throw new Error("No invoice selected");
    }
    return downloadAccountingInvoiceDocument(
      userSession.token.accessToken,
      activeWorkspace.workspaceId,
      openedInvoice.id,
    );
  };

  const handlePreviewInvoice = async () => {
    setIsLoadingPreview(true);
    try {
      const html = await loadInvoicePreview();
      setPreviewHtml(html);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error("Error previewing invoice:", error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handlePrintInvoice = async () => {
    setIsLoadingPreview(true);
    try {
      const html = await loadInvoicePreview();
      const printFrame = document.createElement("iframe");
      printFrame.setAttribute("aria-hidden", "true");
      printFrame.style.position = "fixed";
      printFrame.style.width = "0";
      printFrame.style.height = "0";
      printFrame.style.border = "0";
      printFrame.onload = () => {
        printFrame.contentWindow?.focus();
        printFrame.contentWindow?.print();
        window.setTimeout(() => printFrame.remove(), 1000);
      };
      printFrame.srcdoc = html;
      document.body.appendChild(printFrame);
    } catch (error) {
      console.error("Error printing invoice:", error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!openedInvoice || !userSession) return;
    setIsDownloading(true);
    try {
      const response = await downloadInvoice(
        userSession.token.accessToken,
        openedInvoice.id,
      );
      if (!(response instanceof Blob)) {
        throw new Error("Invalid invoice download response");
      }
      const url = window.URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${
          openedInvoice.issuanceMode === "accounting_provider"
            ? "invoice-basis"
            : "invoice"
        }-${openedInvoice.id}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUpdateInvoice = async (
    updatedItems: IInvoiceItem[],
    companyId: string,
  ) => {
    if (!openedInvoice || !userSession) return;

    setIsUpdating(true);
    try {
      // Convert any string values to numbers for calculations
      const normalizedItems = updatedItems.map((item) => ({
        ...item,
        quantity: typeof item.quantity === "string" ? 0 : item.quantity,
        price: typeof item.price === "string" ? 0 : item.price,
        vat: typeof item.vat === "string" ? 0 : item.vat || 0,
        discountInPercent:
          typeof item.discountInPercent === "string"
            ? 0
            : item.discountInPercent || 0,
      }));

      // Calculate new amounts based on updated items
      const totalAmountWithoutVat = normalizedItems.reduce(
        (sum: number, item: IInvoiceItem) => {
          const basePrice = item.quantity * item.price;
          const discount = item.discountInPercent
            ? (basePrice * item.discountInPercent) / 100
            : 0;
          return sum + basePrice - discount;
        },
        0,
      );

      const totalVatAmount = normalizedItems.reduce(
        (sum: number, item: IInvoiceItem) => {
          const basePrice = item.quantity * item.price;
          const discount = item.discountInPercent
            ? (basePrice * item.discountInPercent) / 100
            : 0;
          const priceAfterDiscount = basePrice - discount;
          return sum + (item.vat ? (priceAfterDiscount * item.vat) / 100 : 0);
        },
        0,
      );

      const totalAmount = totalAmountWithoutVat + totalVatAmount;

      // Prepare update payload
      const updatePayload = {
        items: normalizedItems,
        amounts: {
          totalAmount,
          totalAmountWithoutVat,
          totalVatAmount,
        },
        companyId,
        customerId:
          openedInvoice.customerId || openedInvoice.invoicee?.id || "",
        dueDate: openedInvoice.dueDate,
        invoiceDate: openedInvoice.invoiceDate,
        workspaceId: activeWorkspace?.workspaceId ?? "",
      };

      const response = await updateInvoice(
        userSession.token.accessToken,
        openedInvoice.id,
        updatePayload,
      );

      if (!response || !response.success) {
        throw new Error(response?.error || "Failed to update invoice");
      }

      setShowEditInvoiceDialog(false);
      setSuccessToastText(
        t("invoice:actions.update_success", "Invoice updated successfully"),
      );
      props.onRefresh();
    } catch (error) {
      console.error("Error updating invoice:", error);
      // TODO: Add user-facing error notification here
    } finally {
      setIsUpdating(false);
    }
  };

  const confirmDiscard = async () => {
    if (!discardTarget || !accessToken || discardRunning.current) return;
    discardRunning.current = true;
    setIsDiscarding(true);
    setDiscardError("");
    try {
      await discardInvoice(accessToken, discardTarget.id);
      setDiscardTarget(undefined);
      updateOpenedInvoice(undefined);
      invalidateInvoices();
      invalidateOrders();
      props.onRefresh();
      setSuccessToastText(t("invoice:discard.success"));
    } catch (error) {
      setDiscardError(invoiceApiError(error, t("invoice:discard.error")));
    } finally {
      discardRunning.current = false;
      setIsDiscarding(false);
    }
  };

  return (
    <>
      <Dialog
        show={!!discardTarget}
        title={t("invoice:discard.title", {
          number: discardTarget?.invoiceNumber,
        })}
        content={
          <div className="space-y-3">
            <p>{t("invoice:discard.description")}</p>
            {discardError && (
              <p role="alert" className="text-danger">
                {discardError}
              </p>
            )}
          </div>
        }
        buttonConfirmText={t("invoice:discard.action")}
        confirmButtonColor="failure"
        confirmButton={confirmDiscard}
        isLoading={isDiscarding}
        dismissible={!isDiscarding}
        toggleModal={() => {
          if (!discardRunning.current) setDiscardTarget(undefined);
        }}
      />
      <SplitTable
        ref={splitTableRef}
        isLoading={props.isLoading}
        tableRows={tableRows}
        tableFooter={props.tableFooter}
        onRowClick={(invoice: Invoice | undefined) =>
          updateOpenedInvoice(invoice)
        }
        content={
          <InvoiceDetails
            invoice={openedInvoice}
            loadDocumentPreview={loadInvoicePreview}
            loadProviderDocument={loadProviderDocument}
            accountingStatus={accountingDetails}
            accountingConnection={accountingConnection}
            accountingStatusLoading={isLoadingAccountingDetails}
            accountingStatusRefreshing={isRefreshingAccountingDetails}
            onRefreshAccountingStatus={refreshAccountingDetails}
            sendInvoice={() => {
              setShowSendInvoiceDialog(true);
            }}
          />
        }
        topRightContent={
          <div className="flex items-center gap-2">
            {openedInvoice && (
              <>
                <DropdownButton
                  placement="bottom-end"
                  buttonText={t("invoice:actions.download_menu")}
                  buttonPrependIcon={
                    <HiOutlineDownload className="mr-1.5 h-4 w-4" />
                  }
                  buttonAppendIcon={<HiOutlineChevronDown className="ml-1" />}
                  buttonSize="sm"
                  buttonVariant="secondary"
                  className="!ml-0"
                  menuClassName="min-w-[11rem]"
                  items={[
                    {
                      text: (
                        <p className="flex min-w-[9rem] items-center gap-2 px-4 py-3">
                          {isDownloading ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
                          ) : (
                            <HiOutlineDownload className="h-5 w-5" />
                          )}
                          {t("invoice:actions.pdf_format")}
                        </p>
                      ),
                      onClick: handleDownloadInvoice,
                    },
                    {
                      text: (
                        <p className="flex min-w-[9rem] items-center gap-2 px-4 py-3">
                          {isDownloadingCsv ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
                          ) : (
                            <HiOutlineDownload className="h-5 w-5" />
                          )}
                          {t("invoice:actions.csv_format")}
                        </p>
                      ),
                      onClick: handleDownloadCsv,
                    },
                  ]}
                />
                <DropdownButton
                  placement="bottom-end"
                  buttonText={t("invoice:actions.buttonText")}
                  buttonAppendIcon={<HiOutlineChevronDown className="ml-2" />}
                  buttonSize="sm"
                  buttonVariant="primary"
                  className="!ml-0 bg-brand text-white hover:bg-lgb-blue-350"
                  menuClassName="min-w-[18rem]"
                  items={[
                    {
                      text: (
                        <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3">
                          <HiOutlineEye className="h-5 w-5" />
                          {t(
                            openedInvoice.issuanceMode === "accounting_provider"
                              ? "invoice:actions.preview_basis"
                              : "invoice:actions.preview",
                          )}
                        </p>
                      ),
                      onClick: handlePreviewInvoice,
                    },
                    {
                      text: (
                        <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3">
                          {isLoadingPreview ? (
                            <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
                          ) : (
                            <HiOutlinePrinter className="h-5 w-5" />
                          )}
                          {t("invoice:actions.print")}
                        </p>
                      ),
                      onClick: handlePrintInvoice,
                    },
                    ...(canCreateInTripletex || canRecoverTripletex
                      ? [
                          {
                            text: (
                              <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3">
                                {isPreparingTripletex ? (
                                  <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-brand" />
                                ) : (
                                  <HiOutlinePaperAirplane className="h-5 w-5" />
                                )}
                                {t(
                                  isPreparingTripletex
                                    ? "invoice:accounting_integration.preparing"
                                    : canRecoverTripletex
                                      ? "invoice:accounting_integration.recovery_action"
                                      : "invoice:accounting_integration.create_action",
                                )}
                              </p>
                            ),
                            onClick: () => {
                              void prepareTripletexInvoice(false);
                            },
                          },
                        ]
                      : []),
                    ...(hasIntegrationAccountingRecord
                      ? [
                          {
                            text: (
                              <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3">
                                <HiOutlineRefresh
                                  className={`h-5 w-5 ${
                                    isRefreshingAccountingDetails
                                      ? "animate-spin"
                                      : ""
                                  }`}
                                />
                                {t(
                                  "invoice:accounting_integration.refresh_status",
                                )}
                              </p>
                            ),
                            onClick: refreshAccountingDetails,
                          },
                        ]
                      : []),
                    ...(inferredIssuanceMode === "accounting_provider" &&
                    accountingIntegrationAvailable &&
                    accountingConnection?.status !== "active"
                      ? [
                          {
                            text: (
                              <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3">
                                <HiOutlineCog className="h-5 w-5" />
                                {t(
                                  "invoice:accounting_integration.manage_integration",
                                )}
                              </p>
                            ),
                            onClick: () =>
                              navigate("/settings?tab=integrations"),
                          },
                        ]
                      : []),
                    ...(canManageInvoices &&
                    openedInvoice &&
                    !openedInvoice.isSent &&
                    isInvoiceDraft(openedInvoice) &&
                    !openedInvoice.isPaid
                      ? [
                          {
                            text: (
                              <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3">
                                <HiOutlinePencil className="h-5 w-5" />
                                {t(
                                  "invoice:actions.edit_invoice",
                                  "Edit Invoice",
                                )}
                              </p>
                            ),
                            onClick: () => {
                              setShowEditInvoiceDialog(true);
                            },
                          },
                        ]
                      : []),
                    ...(canManageInvoices &&
                    canDiscardInvoice(openedInvoice) &&
                    !hasIntegrationAccountingRecord
                      ? [
                          {
                            text: (
                              <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3 text-danger">
                                <HiOutlineTrash className="h-5 w-5" />
                                {t("invoice:discard.action")}
                              </p>
                            ),
                            onClick: () => {
                              setDiscardError("");
                              setDiscardTarget(openedInvoice);
                            },
                          },
                        ]
                      : []),
                    ...(canManageAccountingRegistration &&
                    openedInvoice?.lifecycleStatus !== "discarded" &&
                    isInvoiceFinalized(openedInvoice) &&
                    !hasIntegrationAccountingRecord
                      ? [
                          {
                            text: (
                              <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3">
                                <HiOutlineCheckCircle className="h-5 w-5 shrink-0" />
                                {t(
                                  openedInvoice.accountingState === "confirmed"
                                    ? "invoice:actions.revert_accounting_registration"
                                    : "invoice:actions.mark_registered_in_accounting",
                                )}
                              </p>
                            ),
                            onClick: () =>
                              setShowAccountingRegistrationDialog(true),
                          },
                        ]
                      : []),
                    ...(canManageInvoices &&
                    openedInvoice &&
                    !hasInvoiceDelivery &&
                    canOfferDeliveryActions
                      ? [
                          {
                            text: (
                              <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3">
                                <HiOutlinePaperAirplane className="h-5 w-5" />
                                {t("common:send")}
                              </p>
                            ),
                            onClick: () => {
                              openSendInvoiceDialog();
                            },
                          },
                        ]
                      : []),
                    ...(canManageInvoices &&
                    openedInvoice &&
                    hasInvoiceDelivery &&
                    canOfferDeliveryActions
                      ? [
                          {
                            text: (
                              <p className="flex min-w-[17rem] items-center gap-2 whitespace-nowrap px-4 py-3">
                                <HiOutlinePaperAirplane className="h-5 w-5" />
                                {t("common:resend")}
                              </p>
                            ),
                            onClick: () => {
                              openSendInvoiceDialog();
                            },
                          },
                        ]
                      : []),
                  ]}
                />
              </>
            )}
          </div>
        }
        tableHeaders={[
          {
            id: "invoice_number",
            children: t("invoice:details.invoice"),
            showOnSmallScreen: true,
          },
          {
            id: "customer",
            children: t("common:customer"),
            showOnSmallScreen: true,
          },
          {
            id: "status",
            children: t("common:status"),
            showOnSmallScreen: true,
          },
          {
            id: "accounting_status",
            children: t("invoice:accounting_status.column"),
            showOnSmallScreen: false,
            hidden: openedInvoice !== undefined,
          },
          {
            id: "due_date",
            children: t("invoice:details.due_date"),
            showOnSmallScreen: true,
            hidden: hideColumns,
          },
          {
            id: "sum_total",
            children: t("invoice:pdf.sum_total"),
            showOnSmallScreen: false,
            hidden: openedInvoice !== undefined,
          },
        ]}
      />
      <SendInvoice
        show={showSendInvoiceDialog}
        isLoading={isSending || isPreparingTripletex}
        isResend={hasInvoiceDelivery}
        initialIssuanceMode={
          isRecoveringAccountingDelivery
            ? "logibud"
            : (inferredIssuanceMode ??
              (canIssueDraftThroughTripletex
                ? "accounting_provider"
                : "logibud"))
        }
        lockedIssuanceMode={
          isRecoveringAccountingDelivery ? "logibud" : inferredIssuanceMode
        }
        accountingCopyOnly={isRecoveringAccountingDelivery}
        recoveryMessage={
          isRecoveringAccountingDelivery
            ? t("invoice:actions.accounting_copy_delivery_recovery")
            : undefined
        }
        customerEmail={
          openedInvoice?.invoicee?.invoiceEmail ||
          openedInvoice?.invoicee?.contactEmail ||
          undefined
        }
        accountingEmail={
          companies.find((company) => company.id === openedInvoice?.companyId)
            ?.accountingSettings?.accountingEmail ||
          openedInvoice?.invoicer?.accountingSettings?.accountingEmail ||
          undefined
        }
        defaultSendToAccounting={
          companies.find((company) => company.id === openedInvoice?.companyId)
            ?.accountingSettings?.forwardInvoice ??
          openedInvoice?.invoicer?.accountingSettings?.forwardInvoice ??
          false
        }
        integratedAccountingProvider={
          canIssueDraftThroughTripletex ? "tripletex" : undefined
        }
        accountingIntegrationLoading={
          Boolean(openedInvoice) &&
          isInvoiceDraft(openedInvoice) &&
          isLoadingAccountingDetails
        }
        toggleModal={closeSendInvoiceDialog}
        confirmButton={handleSendInvoice}
      />
      <EditInvoice
        show={showEditInvoiceDialog}
        isLoading={isUpdating}
        invoice={openedInvoice}
        companies={companies}
        toggleModal={() => setShowEditInvoiceDialog(!showEditInvoiceDialog)}
        confirmButton={handleUpdateInvoice}
      />
      <TripletexInvoiceDialog
        show={showTripletexDialog}
        preview={tripletexPreview}
        allowCustomerCreate={allowTripletexCustomerCreate}
        isSubmitting={isSubmittingTripletex}
        errorMessage={tripletexActionError}
        onAllowCustomerCreateChange={setAllowTripletexCustomerCreate}
        onClose={() => {
          if (!isSubmittingTripletex) {
            setShowTripletexDialog(false);
            setTripletexActionError(null);
            setTripletexFinalizeDraft(false);
          }
        }}
        onSubmit={createTripletexInvoice}
      />
      <DialogNotify
        show={showAccountingRegistrationDialog}
        isLoading={isUpdatingAccountingRegistration}
        size="md"
        title={t(
          openedInvoice?.accountingState === "confirmed"
            ? "invoice:actions.revert_accounting_registration_title"
            : "invoice:actions.mark_registered_in_accounting_title",
        )}
        text={t(
          openedInvoice?.accountingState === "confirmed"
            ? "invoice:actions.revert_accounting_registration_description"
            : "invoice:actions.mark_registered_in_accounting_description",
        )}
        buttonCloseText={t("common:cancel")}
        buttonConfirmText={t("common:confirm")}
        toggleModal={() => setShowAccountingRegistrationDialog(false)}
        confirmButton={toggleAccountingRegistration}
      />
      <Modal
        root={modalPortalRoot}
        show={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        size="7xl"
      >
        <Modal.Header>
          <div className="text-xl font-semibold text-text">
            {t(
              openedInvoice?.issuanceMode === "accounting_provider"
                ? "invoice:actions.preview_basis_dialog_title"
                : "invoice:actions.preview_dialog_title",
            )}
          </div>
        </Modal.Header>
        <Modal.Body className="p-0 overflow-hidden">
          {isLoadingPreview ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white"></div>
            </div>
          ) : (
            <div className="h-[calc(100vh-12rem)] overflow-hidden">
              {previewHtml ? (
                <iframe
                  srcDoc={previewHtml}
                  className="w-full h-full border-0 min-w-[800px]"
                  title={t(
                    openedInvoice?.issuanceMode === "accounting_provider"
                      ? "invoice:actions.preview_basis_dialog_title"
                      : "invoice:actions.preview_dialog_title",
                  )}
                  sandbox="allow-same-origin"
                  style={{
                    width: "100%",
                    minWidth: "800px",
                    overflow: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                />
              ) : (
                <div className="flex justify-center items-center h-64 text-gray-500">
                  {t("invoice:actions.preview_error")}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="gray"
            onClick={() => setShowPreviewDialog(false)}
            disabled={isLoadingPreview}
          >
            {t("common:close")}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        root={modalPortalRoot}
        show={showAccountingNumberDialog}
        onClose={() => setShowAccountingNumberDialog(false)}
        size="md"
      >
        <Modal.Header>
          {t("invoice:actions.accounting_customer_number_title")}
        </Modal.Header>
        <Modal.Body>
          <p className="mb-4 text-sm text-muted">
            {t("invoice:actions.accounting_customer_number_description", {
              customer: openedInvoice?.invoicee
                ? Customer.getCustomerName(openedInvoice.invoicee)
                : "",
            })}
          </p>
          <StyledTextInput
            id="invoiceAccountingCustomerNumber"
            placeholder={t(
              "invoice:actions.accounting_customer_number_placeholder",
            )}
            value={accountingCustomerNumber}
            onInputChanged={setAccountingCustomerNumber}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={saveAccountingNumberAndDownload}
            disabled={isSavingAccountingNumber}
          >
            {t("invoice:actions.save_and_download")}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowAccountingNumberDialog(false)}
            disabled={isSavingAccountingNumber}
          >
            {t("common:cancel")}
          </Button>
        </Modal.Footer>
      </Modal>
      {successToastText && (
        <div className="fixed top-5 right-5 z-50">
          <Toast className="!bg-green-100 dark:!bg-green-700 !text-green-700 dark:!text-white min-w-[350px] max-w-[450px] shadow-lg">
            <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-500 text-white dark:bg-green-600 dark:text-white">
              <HiCheck className="h-6 w-6" />
            </div>
            <div className="ml-3 text-base font-medium mr-8">
              <p className="text-green-700 dark:text-white">
                {successToastText}
              </p>
            </div>
            <Toast.Toggle
              onClick={() => setSuccessToastText("")}
              className="absolute top-2 right-2 bg-transparent text-green-700 hover:text-green-900 rounded-lg focus:ring-2 focus:ring-green-300 p-1.5 hover:bg-green-100/50 inline-flex h-8 w-8 dark:bg-transparent dark:text-white dark:hover:text-white dark:hover:bg-green-700/40"
            />
          </Toast>
        </div>
      )}
      {errorToastText && (
        <div className="fixed top-5 right-5 z-50">
          <Toast className="!bg-red-100 dark:!bg-red-700 !text-red-700 dark:!text-white min-w-[350px] max-w-[450px] shadow-lg">
            <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-500 text-white dark:bg-red-600 dark:text-white">
              <HiOutlineExclamationCircle className="h-6 w-6" />
            </div>
            <div className="ml-3 text-base font-medium mr-8">
              <p className="text-red-700 dark:text-white">{errorToastText}</p>
            </div>
            <Toast.Toggle
              onClick={() => setErrorToastText("")}
              className="absolute top-2 right-2 bg-transparent text-red-700 hover:text-red-900 rounded-lg focus:ring-2 focus:ring-red-300 p-1.5 hover:bg-red-100/50 inline-flex h-8 w-8 dark:bg-transparent dark:text-white dark:hover:text-white dark:hover:bg-red-700/40"
            />
          </Toast>
        </div>
      )}
    </>
  );
};
